import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Recording } from "../types";
import { apiFetch } from "../utils/api";
import {
  recoveryActionButtonClassName,
  recoveryActionExplainerClassName,
  recoveryActionRowClassName,
} from "./recoveryActionLayout";

interface RecordingAudioPlaybackProps {
  conversationId: string;
  recording?: Recording | null;
  label: string;
}

interface RecordingRetranscriptionActionProps {
  conversationId: string;
  recording?: Recording | null;
  onRetranscriptionStarted?: (workflowId: string) => void;
}

export const RecordingRetranscriptionAction: React.FC<
  RecordingRetranscriptionActionProps
> = ({ conversationId, recording, onRetranscriptionStarted }) => {
  const [isRetranscribing, setIsRetranscribing] = useState(false);
  const canRetranscribe = Boolean(
    recording?.is_active &&
    recording.has_raw_audio &&
    recording.can_retranscribe,
  );
  const hasSuccessfulTranscript = Boolean(
    recording?.text ||
    recording?.redacted_transcript ||
    recording?.executive_summary,
  );

  const handleRetranscribe = async () => {
    if (!recording || !canRetranscribe) {
      return;
    }

    if (hasSuccessfulTranscript) {
      const confirmed = window.confirm(
        "Weet je zeker dat je opnieuw wilt samenvatten? " +
          "De bestaande transcriptie en samenvattingen worden vervangen als dit lukt.",
      );
      if (!confirmed) {
        return;
      }
    }

    setIsRetranscribing(true);
    try {
      const response = await apiFetch(
        `/conversation/${conversationId}/recordings/${recording.recording_type}/retranscribe`,
        { method: "POST" },
      );
      if (!response.ok) {
        throw new Error(`Re-transcription failed: ${response.status}`);
      }

      const data = await response.json();
      if (data.workflow_id && onRetranscriptionStarted) {
        onRetranscriptionStarted(data.workflow_id);
      }
    } catch (error) {
      console.error("Could not start re-transcription:", error);
    } finally {
      setIsRetranscribing(false);
    }
  };

  if (!canRetranscribe) {
    return null;
  }

  return (
    <div className={recoveryActionRowClassName}>
      <button
        type="button"
        onClick={handleRetranscribe}
        disabled={isRetranscribing}
        className={recoveryActionButtonClassName}
      >
        <RotateCcw className="h-4 w-4" aria-hidden="true" />
        {isRetranscribing
          ? "Opnieuw samenvatten gestart..."
          : "Opnieuw samenvatten"}
      </button>
      <p className={recoveryActionExplainerClassName}>
        Vervangt de bestaande transcriptie en samenvattingen.
      </p>
    </div>
  );
};

const RecordingAudioPlayback: React.FC<RecordingAudioPlaybackProps> = ({
  conversationId,
  recording,
  label,
}) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const canPlay = Boolean(
    recording?.is_active && recording.has_raw_audio && recording.can_play_audio,
  );

  useEffect(() => {
    if (!canPlay || !recording) {
      setAudioUrl(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    const loadAudio = async () => {
      try {
        const response = await apiFetch(
          `/conversation/${conversationId}/recordings/${recording.recording_type}/audio`,
        );
        if (!response.ok) {
          return;
        }

        const audioBlob = await response.blob();
        if (cancelled) {
          return;
        }

        objectUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(objectUrl);
      } catch (error) {
        console.error("Could not load recording audio:", error);
      }
    };

    loadAudio();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [canPlay, conversationId, recording]);

  if (!canPlay || !audioUrl) {
    return null;
  }

  return (
    <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
      <p className="text-sm font-medium text-gray-700 mb-2">
        Opname beluisteren
      </p>
      <audio
        aria-label={`${label} afspelen`}
        controls
        controlsList="nodownload"
        preload="none"
        src={audioUrl}
        className="w-full"
      />
    </div>
  );
};

export default RecordingAudioPlayback;
